import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { InceptionTypeInputEnum } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { useCreateProduct } from '@/modules/admin-center/hooks/useCreateProduct'
import useCreateBuyListProduct from '@/modules/buy-lists/pages/buy-list/hooks/useCreateBuyListProduct'
import { useCreateOrUpdatePartyProduct } from '@/modules/products/hooks/useCreateOrUpdatePartyProduct'
import { CreateProductFormInputs } from '@/modules/shared/components/create-product-modal/CreateProductFormSchema'
import { UseCreateAndAddProductProps } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductRequisition'
import { displayToast } from '@/modules/shared/components/toast/displayToast'
import { useAlert } from '@/modules/shared/hooks/useAlert'

export function useCreateAndAddProductBuyList({ quickAdd, refetch, onCloseModal, id }: UseCreateAndAddProductProps) {
  const { currentOrganisation } = useSessionUser()
  const { t } = useTranslation()
  const [createProduct, { loading: createProductLoading }] = useCreateProduct()
  const [createBuyListProduct, { loading: createBuyListProductLoading }] = useCreateBuyListProduct()
  const navigate = useNavigate()
  const { alertDialog } = useAlert()
  const [createOrUpdatePartlyProduct] = useCreateOrUpdatePartyProduct()

  const onCreateAndAddProductBuyList = (input: CreateProductFormInputs, isAPurchaser?: boolean) => {
    const {
      categoryId,
      brand,
      itemDescription,
      itemMeasure,
      itemSize,
      itemPackName,
      itemSellPackName,
      itemSellQuantity,
      code,
      externalId,
    } = input
    createProduct({
      variables: {
        input: {
          categoryId,
          brand,
          itemDescription,
          locale: 'en',
          itemMeasure,
          itemSize,
          itemPackName: itemPackName === 'null' ? null : itemPackName,
          itemSellPackName,
          itemSellQuantity,
          externalId,
          inceptionType: InceptionTypeInputEnum.Manual,
          ownerCode: isAPurchaser ? code : undefined,
        },
      },
      onCompleted: async (data) => {
        const createdProduct = data.createProduct.product
        if (!createdProduct?.id) {
          alertDialog({ type: 'error' })
          return
        }
        const createdProductId = Number(createdProduct.id)
        if (code) {
          await createOrUpdatePartlyProduct({
            variables: {
              input: {
                productId: createdProductId,
                organisationId: Number(currentOrganisation?.id),
                code: code,
              },
            },
          })
        }
        createBuyListProduct({
          variables: { input: { buyListId: Number(id), productId: createdProductId } },
          onCompleted: () => {
            if (quickAdd) {
              onCloseModal && onCloseModal()
              displayToast({ title: t('general.productAdded', 'Product Added') })
              refetch && refetch()
            } else {
              navigate(generatePath('/buy-lists/:buyListId/products', { buyListId: String(id) }))
            }
          },
        })
      },
      onError() {
        alertDialog({ type: 'error' })
      },
    })
  }
  return {
    onCreateAndAddProductBuyList,
    loading: createProductLoading || createBuyListProductLoading,
  }
}
